import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { LinkPageOutlink } from 'common-ts/dist/models/LinkPage';
import { marketplaceLogoDictionary } from './PageComponentElementProduct';

// The Linktree-style button list of the /link page. Marketplace-flagged
// links are intercepted (parent opens the "yakin?" exit dialog) instead of
// navigating straight out.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  linkButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 18px',
    borderRadius: '12px',
    border: '1px solid #d9d9d9',
    backgroundColor: '#ffffff',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'transform 120ms ease',
    '&:hover': {
      transform: 'scale(1.015)',
    },
  },
  icon: {
    width: '26px',
    height: '26px',
    borderRadius: '6px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    flexGrow: 1,
    textAlign: 'center',
    // Optical centering: pad the text area so an icon on the left doesn't
    // shift the label.
    marginRight: '26px',
  },
  titleNoIcon: {
    marginRight: 0,
  },
}),
);

// Auto-resolve a marketplace logo from the outlink's host when the merchant
// didn't set an icon (shopee/tokopedia/tiktok).
export function resolveOutlinkIcon(link: LinkPageOutlink): string | null {
  if (link.iconUrl) return link.iconUrl;
  try {
    const host = new URL(link.url).hostname.toLowerCase();
    for (const [name, logo] of Object.entries(marketplaceLogoDictionary)) {
      if (host.includes(name)) return logo;
    }
  } catch {
    // Bad URLs are already rejected server-side; be lenient here.
  }
  return null;
}

interface LinkPageOutlinkListProps {
  outlinks: LinkPageOutlink[];
  textColor?: string;
  // Marketplace-flagged links go through the exit-interception dialog.
  onMarketplaceClick: (link: LinkPageOutlink) => void;
}

export default function LinkPageOutlinkList({ outlinks, textColor, onMarketplaceClick }: LinkPageOutlinkListProps) {
  const classes = useStyles();

  const ordered = [...outlinks].sort((a, b) => a.position - b.position);
  if (!ordered.length) return null;

  return (
    <Box className={classes.list}>
      {
        ordered.map((link) => {
          const icon = resolveOutlinkIcon(link);
          return (
            <a
              key={link.id}
              className={classes.linkButton}
              style={textColor ? { color: textColor, borderColor: textColor } : undefined}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                if (link.isMarketplace) {
                  event.preventDefault();
                  onMarketplaceClick(link);
                }
              }}
            >
              {icon && <img className={classes.icon} src={icon} alt="" />}
              <Typography className={classes.title + (icon ? '' : ' ' + classes.titleNoIcon)}>
                {link.title}
              </Typography>
            </a>
          );
        })
      }
    </Box>
  );
}
